import {ContainerStatusEntity} from "@ots-frontend/ots-data";
import {GridCellKind} from "@glideapps/glide-data-grid";
import type {ColumnSort} from "@ots-frontend/ots-ui-tw";
// eslint-disable-next-line @nx/enforce-module-boundaries -- Shared theme-aware header sprites.
import {getDataGridSortIcons} from "@ots-frontend/ots-ui-tw";
import {useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {ContainerStatusDataGridProps} from "./ContainerStatusDataGrid";
import {UseEntityDataGridProps} from "../EntityDataGrid/useEntityDataGrid";
import {useDataGrid} from "../DataGrid/useDataGrid";
import {useDataGridColumnLayout} from "../DataGrid/useDataGridColumnLayout";
import {ContainerStatusColumn, useContainerStatusDataGridCols} from "./useContainerStatusDataGridCols";
import type {ContainerStatusIconSet, ContainerStatusIconVariant} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";

export type ContainerStatusDataGridColOptions = {
  /** Offers the column on this page; a column the page leaves off isn't in the column picker either. */
  showCompanyCol?: boolean
  showDispatchCol?: boolean
  showPickupCol?: boolean
  showSiteVolumeCol?: boolean
  /** Offered unless set to false, e.g. in DispatchGo, where the viewer is the hauler. */
  showAssignedHaulerCol?: boolean
  statusIconVariant?: ContainerStatusIconVariant
  /** Overrides the set from ContainerStatusIconSetProvider. */
  statusIconSet?: ContainerStatusIconSet
}

export const EMPTY_ARRAY: ContainerStatusEntity[] = []

export const DEFAULT_CONTAINER_STATUS_COLUMN_STORAGE_KEY = "ots.container-status-grid.columns"

export type UseContainerStatusDataGridProps =
  Omit<UseEntityDataGridProps<ContainerStatusEntity>, 'colsMap'>
  & Pick<ContainerStatusDataGridProps, 'colOptions' | 'cargoSubtypes' | 'columnStorageKey'>
  & {colorScheme?: "light" | "dark", isStalePoll?: (status: ContainerStatusEntity | undefined) => boolean}

/**
 * Merges each run of adjacent, visible measurement columns into one message cell for containers whose readings
 * were omitted. Runs are found after the layout is applied, so hiding or moving a column keeps the spans right.
 */
const mergeOmittedMeasurements = (
  columns: ContainerStatusColumn[],
  messageFor: (status: ContainerStatusEntity) => string | undefined,
): ContainerStatusColumn[] => {
  const merged = [...columns]
  let start = 0
  while (start < merged.length) {
    const group = merged[start].measurementGroup
    let end = start
    while (group && end + 1 < merged.length && merged[end + 1].measurementGroup === group) end++

    if (group) {
      const runStart = start
      const runEnd = end
      for (let index = runStart; index <= runEnd; index++) {
        const column = merged[index]
        const getContent = column.getContent
        merged[index] = {
          ...column,
          getContent: (status, col) => {
            const message = messageFor(status)
            if (!message) {
              return getContent(status, col)
            }

            const failed = status.metadata?.measurements?.omissionReason === "ENRICHMENT_FAILED"
            return {
              kind: GridCellKind.Text,
              data: message,
              displayData: index === runStart ? message : "",
              allowOverlay: false,
              span: [runStart, runEnd],
              themeOverride: {
                bgCell: failed ? "#FDECEC" : "#F3F4F6",
                textDark: failed ? "#B42318" : "#667085",
                baseFontStyle: "italic 12px",
              },
            }
          }
        }
      }
    }

    start = end + 1
  }
  return merged
}

export function useContainerStatusDataGrid(props: UseContainerStatusDataGridProps) {
  const {
    data = EMPTY_ARRAY,
    colOptions,
    colWidths,
    cargoSubtypes,
    sort,
    columnStorageKey = DEFAULT_CONTAINER_STATUS_COLUMN_STORAGE_KEY,
    colorScheme = "light",
    isStalePoll,
  } = props;

  const {t} = useTranslation(["common"])

  const {colsMap} = useContainerStatusDataGridCols({
    colWidths: colWidths,
    colOptions: colOptions,
    cargoSubtypes: cargoSubtypes,
    isStalePoll: isStalePoll,
    colorScheme: colorScheme,
  })

  const storageKey = `${columnStorageKey}.v1`
  // the legacy positional sort seeds the layout once; later header clicks are owned by the layout
  const initialSorts = useRef(new Map<string, ColumnSort | undefined>())
  if (!initialSorts.current.has(storageKey)) {
    const column = sort && colsMap.filter((column) => column.defaultVisible !== false)[sort.col]
    initialSorts.current.set(storageKey, column && sort ? {id: column.id, direction: sort.direction} : undefined)
  }

  const columnLayout = useDataGridColumnLayout(colsMap, storageKey, {
    defaultSort: initialSorts.current.get(storageKey),
  })

  const columns = useMemo(() => {
    const messageFor = (status: ContainerStatusEntity): string | undefined => {
      if (status.metadata?.measurements?.included !== false) {
        return undefined
      }

      switch (status.metadata.measurements.omissionReason) {
        case "NOT_REQUESTED":
          return t("entity.containerstatus.datagrid.measurements.notincluded", "Tank readings shown only for assigned containers")
        case "CONNECTION_UNAVAILABLE":
          return t("entity.containerstatus.datagrid.measurements.unavailable", "Tank readings unavailable")
        case "ENRICHMENT_FAILED":
          return t("entity.containerstatus.datagrid.measurements.failed", "Tank readings failed to load")
        default:
          return t("entity.containerstatus.datagrid.measurements.omitted", "Tank readings not available")
      }
    }

    return mergeOmittedMeasurements(columnLayout.visibleColumns as ContainerStatusColumn[], messageFor)
  }, [columnLayout.visibleColumns, t])

  const dataGrid = useDataGrid<ContainerStatusEntity>({
    colsMap: columns,
    data: data,
    columnSort: columnLayout.columnSort,
    onColumnResize: columnLayout.onColumnResize,
  })

  const headerIcons = useMemo(() => getDataGridSortIcons(colorScheme), [colorScheme])
  const displayedColumns = useMemo(
    () =>
      dataGrid.columns.map((column, index) => ({
        ...column,
        icon: dataGrid.sort?.col === index ? `ots-sort-${dataGrid.sort.direction}` : column.icon,
      })),
    [dataGrid.columns, dataGrid.sort]
  )

  return {
    ...dataGrid,
    columns: displayedColumns,
    headerIcons,
    onColumnMoved: columnLayout.onColumnMoved,
    columnLayout,
  }
}

export type UseContainerStatusDataGridResult = ReturnType<typeof useContainerStatusDataGrid>
